// Account sagas - main entry point
import { all, fork, takeEvery, takeLatest } from 'redux-saga/effects';

// Auth sagas
import {
  authActions,
  oAuthSaga,
  resetPassword,
  signIn,
  signInWithGoogle,
  signUp,
} from './authSagas';

// Verification sagas
import {
  verificationActions,
  verifyTempUserEmail,
  verifyUserEmail,
} from './verificationSagas';

// Profile sagas
import {
  changeName,
  finishOnboarding,
  profileActions,
  saveAccountDetailsSaga,
} from './profileSagas';

// Session sagas
import { logout, logoutToLogin, sessionActions } from './sessionSagas';

// Search sagas
import { getUsersByNameSaga, searchActions } from './searchSagas';

// Re-export types
export * from './types';

// Re-export constants
export { REDIRECT_PATH } from './constants';

// Re-export individual sagas for direct use
export {
  // Auth
  signIn,
  signUp,
  signInWithGoogle,
  resetPassword,
  oAuthSaga,
  // Verification
  verifyUserEmail,
  verifyTempUserEmail,
  // Profile
  changeName,
  finishOnboarding,
  saveAccountDetailsSaga,
  // Session
  logout,
  logoutToLogin,
  // Search
  getUsersByNameSaga,
};

// Auth watcher saga
function* watchAuthSagas(): Generator<unknown, void, unknown> {
  yield takeEvery(authActions.UserSignIn, signIn);
  yield takeEvery(authActions.UserSignUp, signUp);
  yield takeEvery(authActions.UserSignInWithGoogle, signInWithGoogle);
  yield takeLatest(authActions.UserPasswordReset, resetPassword);
}

// Verification watcher saga
function* watchVerificationSagas(): Generator<unknown, void, unknown> {
  yield takeLatest(verificationActions.UserEmailVerification, verifyUserEmail);
  yield takeLatest(
    verificationActions.UserEmailTempVerification,
    verifyTempUserEmail,
  );
}

// Profile watcher saga
function* watchProfileSagas(): Generator<unknown, void, unknown> {
  yield takeEvery(profileActions.ChangeUsername, changeName);
  yield takeEvery(profileActions.PassUserOnboarding, finishOnboarding);
  yield takeEvery(profileActions.SaveAccountDetails, saveAccountDetailsSaga);
}

// Session watcher saga
function* watchSessionSagas(): Generator<unknown, void, unknown> {
  yield takeEvery(sessionActions.UserLogout, logout);
  yield takeEvery(sessionActions.AccountLogout, logout);
  yield takeEvery(sessionActions.AccountLogoutToLogin, logoutToLogin);
}

// Search watcher saga
function* watchSearchSagas(): Generator<unknown, void, unknown> {
  yield takeLatest(searchActions.UsersSearch, getUsersByNameSaga);
}

// Root account saga combining all watchers
function* accountSaga(): Generator<unknown, void, unknown> {
  yield all([
    fork(watchAuthSagas),
    fork(watchVerificationSagas),
    fork(watchProfileSagas),
    fork(watchSessionSagas),
    fork(watchSearchSagas),
  ]);
}

export default accountSaga;
